import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { useDemo } from "@/context/DemoContext";

export function Eyebrow({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary ${className}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, sub, align = "left", className = "" }) {
  const center = align === "center";
  return (
    <div className={`${center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"} ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-5 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">{sub}</p>}
    </div>
  );
}

export function BookDemoButton({ label = "Book a Demo", type = "demo", testid = "book-demo", className = "" }) {
  const { openDemo } = useDemo();
  return (
    <button
      onClick={() => openDemo(type)}
      data-testid={testid}
      className={`group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:scale-[1.03] hover:shadow-[0_14px_40px_-10px_rgba(16,185,129,0.6)] ${className}`}
    >
      {label}
      <ArrowRight size={17} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

export function LinkButton({ to, children, variant = "outline", testid, className = "" }) {
  const styles =
    variant === "outline"
      ? "border border-white/15 bg-white/[0.02] text-white hover:border-primary/60 hover:bg-white/5"
      : "bg-secondary text-white hover:bg-secondary/70";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all hover:scale-[1.02] ${styles} ${className}`}
    >
      {children}
    </Link>
  );
}

export function DeviceMockup({ src, alt, className = "", spotlight = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden rounded-2xl border border-white/10 bg-card ${className}`}
    >
      {spotlight && (
        <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-2/3 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />
      )}
      <div className="flex items-center gap-1.5 border-b border-white/10 bg-black/40 px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="aspect-[16/10] w-full object-cover" />
    </motion.div>
  );
}
